import React, { useEffect, useState } from 'react';
import { Box, TextField, MenuItem, Select, FormControl, InputLabel, FormHelperText, Button, Typography, IconButton } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { IClientRequest, IClient } from "@ontime/types";
import { selectSelectedOrganization } from '../../stateManagement/selectedOrganizationSlice';
import { useSelector } from 'react-redux';
import ClientService from '../../services/ClientService';
import CustomDialogTitle from '../../customComponents/CustomDialogTitle';
import CustomDialogContent from '../../customComponents/CustomDialogContent';
import CustomFormAction from '../../customComponents/CustomFormAction';
import CustomButton from '../../customComponents/CustomButton';
import InfoComponent from '../../customComponents/InfoComponent';
import ClientList from './ClientList';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import { Gender } from "@ontime/types";

export const clientSchema = z.object({
    clientId: z.string().optional(),
    phone: z.string()
        .max(20, "Maximum length of Phone is 20 characters.")
        .regex(/^\d*$/, "Phone number must contain only digits.")
        .optional(),
    firstName: z.string().min(1, "First Name cannot be empty.").max(50, "Maximum length of First Name is 50 characters."),
    lastName: z.string().max(50, "Maximum length of Last Name is 50 characters.").optional(),
    email: z.string()
        .max(100, "Maximum length of Email is 100 characters.")
        .email("Invalid email format.")
        .optional()
        ,
    code: z.string().max(20, "Maximum length of Code is 20 characters.").optional(),
    dateOfBirth: z.string().optional(),
    note: z.string().max(200, "Maximum length of Note is 200 characters.").optional(),
    gender: z.nativeEnum(Gender).optional(),
}).refine(
    (data) => data.phone?.trim() || data.email?.trim(),
    {
        message: "Either phone or email is required.",
        path: ["phone"], // you could also point to ["email"] or even []
    }
);

interface ClientFormProps {
    clientInput?: IClient | null;
    closeForm: (input?: IClient) => void;
    handleSetClient: (input?: IClient | null) => void;
}

const ClientForm: React.FC<ClientFormProps> = ({ clientInput, closeForm, handleSetClient }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);

    const organization = useSelector(selectSelectedOrganization);

    const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm<IClientRequest>({
        resolver: zodResolver(clientSchema),
        defaultValues: {
            clientId: '',
            phone: '',
            firstName: '',
            lastName: '',
            email: '',
            code: '',
            dateOfBirth: null,
            note: '',
            gender: null,
        },
    });

    useEffect(() => {
        if (clientInput) {
            reset({
                clientId: clientInput.clientId,
                phone: clientInput.phone,
                firstName: clientInput.firstName,
                lastName: clientInput.lastName ?? '',
                email: clientInput.email ?? '',
                code: clientInput.code ?? '',
                dateOfBirth: clientInput.dateOfBirth ?? null,
                note: clientInput.note ?? '',
                gender: clientInput.gender as Gender ?? null,
            });
            if (clientInput.referralId) {
                setReferral({ referralId: clientInput.referralId, referralFullName: clientInput.referralFullName });
            }

        }
        setLoading(false)
    }, [clientInput, reset]);

    const handleSave = async (data: IClientRequest) => {
        setLoading(true);

        // const formData = {
        //     ...data,
        //     organizationId: organization.organizationId
        // };

        try {
            if (clientInput?.clientId) {
                const response = await ClientService.Update(organization?.organizationId ?? '', data);
                closeForm(response);
            } else {
                const response = await ClientService.Create(organization?.organizationId ?? '', data);

                closeForm(response);
            }
        } catch (error) {
            console.error(error, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const referralIsAddable = (): boolean => {
        if (!referral?.referralId && clientInput?.clientId) return true;
        // else if (appointment.userId) return true;
        return false;
    };

    const [openReferral, setOpenReferral] = useState(false);
    const [referral, setReferral] = useState<{ referralId?: string; referralFullName?: string; }>({});

    const [referralLoading, setReferralLoading] = useState(false);

    const handleSelectReferral = async (input?: IClient, prierClientFirstName?: string, prierClientLastName?: string) => {
        // setReferralLoading(true);
        // setOpenReferral(false);

        // try {

        //     const response = await ClientService.UpdateReferralId({ id: clientInput?.id, referralId: input?.id });
        //     if (response.response.success) {
        //         enqueueSnackbar('Saved successfully!', { variant: 'success' });

        //         if (input) {
        //             setReferral({ referralId: input?.id, referralFullName: input.firstName + ' ' + input.lastName });

        //             if (clientInput?.id) {
        //                 handleSetClient({
        //                     ...clientInput,
        //                     referralId: input?.id ?? 0,
        //                     referralFullName: input.firstName + ' ' + input.lastName,
        //                 });
        //             }
        //         }
        //         else {
        //             setReferral({});
        //             if (clientInput?.id) {
        //                 handleSetClient({
        //                     ...clientInput,
        //                     referralId: 0,
        //                     referralFullName: '',
        //                 });
        //             }
        //         }

        //     } else {
        //         enqueueSnackbar(response.response.error?.message, { variant: 'error' });
        //     }

        // } catch (error) {
        //     console.error(error, { variant: 'error' });
        // } finally {
        //     setReferralLoading(false);
        // }
    };

    const handleClose = () => {

        if (openReferral) {
            setOpenReferral(false);
        }
        else {
            closeForm();
        }
    }

    return (
        openReferral ?
            <ClientList state={openReferral ? 'mount' : 'unmount'} loading={loading || referralLoading} setLoading={setLoading} handleSelect={handleSelectReferral} CustomTitle='Select Referral' handleClose={handleClose} hiddenClientId={clientInput?.clientId} />
            :
            (<Box>
                <CustomDialogTitle handleClose={closeForm} title={clientInput?.clientId ? 'Edit client' : 'New client'} loading={loading} />
                <CustomDialogContent>
                    <form onSubmit={handleSubmit(handleSave)} noValidate>
                        <Box mt={2}>
                            <TextField fullWidth label="First Name" InputLabelProps={{ shrink: true }} {...register('firstName')} error={!!errors.firstName} helperText={errors.firstName?.message} />
                        </Box>

                        <Box mt={2}>
                            <TextField fullWidth label="Last Name" InputLabelProps={{ shrink: true }} {...register('lastName')} error={!!errors.lastName} helperText={errors.lastName?.message} />
                        </Box>

                        <Box mt={2}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                label="Phone"
                                InputLabelProps={{ shrink: true }}
                                {...register('phone')}
                                error={!!errors.phone}
                                helperText={errors.phone?.message}
                            />
                        </Box>

                        <Box mt={2}>
                            <TextField fullWidth label="Email" InputLabelProps={{ shrink: true }} {...register('email')} error={!!errors.email} helperText={errors.email?.message} />
                        </Box>

                        <Box mt={2}>
                            <TextField fullWidth label="Code" InputLabelProps={{ shrink: true }} {...register('code')} error={!!errors.code} helperText={errors.code?.message} />
                        </Box>

                        <Box mt={2}>
                            <TextField fullWidth label="Date of Birth" InputLabelProps={{ shrink: true }} type="date" {...register('dateOfBirth')} error={!!errors.dateOfBirth} helperText={errors.dateOfBirth?.message} />
                        </Box>

                        <Box mt={2}>
                            <TextField fullWidth label="Note" InputLabelProps={{ shrink: true }} multiline rows={3} {...register('note')} error={!!errors.note} helperText={errors.note?.message} />
                        </Box>

                        <Box mt={2}>
                            <FormControl fullWidth error={!!errors.gender}>
                                <InputLabel>Gender</InputLabel>
                                <Select
                                    label="Gender"
                                    value={watch('gender') ?? ''}
                                    {...register('gender')}
                                    onChange={(e) => setValue('gender', e.target.value ? (e.target.value as Gender) : null)}
                                >
                                    <MenuItem value={''}>_</MenuItem>
                                    <MenuItem value={Gender.Female}>Female</MenuItem>
                                    <MenuItem value={Gender.Male}>Male</MenuItem>
                                    <MenuItem value={Gender.Other}>Other</MenuItem>
                                </Select>
                                <FormHelperText>{errors.gender?.message}</FormHelperText>
                            </FormControl>
                        </Box>
                        {/*
                        {referralIsAddable() &&
                            <InfoComponent infoContent='Convert guest to registered client'>
                                <Button variant="contained" size='small' onClick={() => setOpenReferral(true)} disabled={loading || referralLoading} ><AssignmentIndIcon /> Add Referral</Button>
                            </InfoComponent>
                        }

                        {referral?.referralId &&
                            <Box display="flex" alignItems="center" gap={0.5} mt={2} height='30px'>
                                <Box display='flex' flexDirection='row'>
                                    <Typography variant="body1" gutterBottom>
                                        Referral:
                                    </Typography>
                                    <AssignmentIndIcon />

                                    <Typography variant="body1" gutterBottom>
                                        {`${referral?.referralFullName}`}
                                    </Typography>

                                </Box>
                                <InfoComponent infoContent='Remove the assigned client'>
                                    <IconButton disabled={referralLoading} size='small' onClick={() => handleSelectReferral(undefined, referral?.referralFullName, '')} ><ClearIcon /></IconButton>
                                </InfoComponent>
                                <InfoComponent infoContent='Change the assigned client'>
                                    <IconButton disabled={referralLoading} size='small' onClick={() => setOpenReferral(true)} ><EditIcon /></IconButton>
                                </InfoComponent>
                            </Box>
                        }
  */}
                        <CustomFormAction>
                            <CustomButton lable='Save' type='submit' disabled={loading} infoContent={`Save the ${clientInput?.clientId ? 'updated' : 'new'} client record`} />
                        </CustomFormAction>
                    </form>
                </CustomDialogContent>
            </Box>)
    );
};

export default ClientForm;